import {
    cloneElement,
    forwardRef,
    ImgHTMLAttributes,
    isValidElement,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useRef,
    useState,
} from "react";
import MuiAvatar, { AvatarProps } from "@mui/material/Avatar";
import useForkRef from "@mui/utils/useForkRef";
import { IconAvatarPlaceholder } from "src/components/icons-mui/avatar-placeholder";
import { useElementFirstSeen } from "src/hooks/use-element-first-seen";
import { useImagePreload } from "src/hooks/use-image-preload";

export interface IWfAvatarProps extends Omit<AvatarProps, "imgProps" | "slotProps" | "children" | "alt"> {
    children?: ReactNode;
    alt?: string;
    loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
    skipImageVerification?: boolean;
}

const useVerifiedImageSrc = (src: string | undefined, isLazy: boolean, rootRef: RefObject<HTMLElement>) => {
    const [canStartLoading, setCanStartLoading] = useState(!isLazy);

    const handleFirstSeen = useCallback(() => setCanStartLoading(true), []);
    useElementFirstSeen(rootRef, isLazy ? handleFirstSeen : undefined);

    const isLoaded = useImagePreload(canStartLoading ? src : undefined);

    return isLoaded ? src : undefined;
};

export const WfAvatar = forwardRef<HTMLDivElement, IWfAvatarProps>(({ alt, children = <IconAvatarPlaceholder />, loading, src, ...rest }, ref) => {
    const { skipImageVerification = false, ...avatarProps } = rest;
    const rootRef = useRef<HTMLDivElement>(null);
    const combinedRef = useForkRef(ref, rootRef);
    const verifiedSrc = useVerifiedImageSrc(skipImageVerification ? undefined : src, !skipImageVerification && loading === "lazy", rootRef);

    const fallback = isValidElement(children)
        ? cloneElement(children as ReactElement<{ role?: string; "aria-label"?: string }>, {
              role: "img",
              "aria-label": alt,
          })
        : children;

    return (
        <MuiAvatar ref={combinedRef} alt={alt} src={skipImageVerification ? src : verifiedSrc} slotProps={{ img: { loading } }} {...avatarProps}>
            {fallback}
        </MuiAvatar>
    );
});

WfAvatar.displayName = "WfAvatar";
